/** A problem+json answer of the API: {type,title,status,detail,code,requestId}. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string;
  readonly requestId: string | null;
  /** For 422 VALIDATION: field path ("pricing.pieces[3].lengthCm") → message. */
  readonly fieldErrors: Record<string, string>;

  constructor(status: number, code: string, detail: string, requestId: string | null = null) {
    super(detail || code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.requestId = requestId;
    this.fieldErrors = code === 'VALIDATION' ? parseValidation(detail) : {};
  }

  is(...codes: string[]): boolean {
    return codes.includes(this.code);
  }
}

/** The network failed or the server answered something that is not the API (proxy down, HTML error page). */
export class NetworkError extends Error {
  constructor(message = 'No hay conexión con el servidor') {
    super(message);
    this.name = 'NetworkError';
  }
}

export function parseValidation(detail: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of detail.split('; ')) {
    const i = part.indexOf(': ');
    if (i <= 0) continue;
    out[part.slice(0, i)] = part.slice(i + 2);
  }
  return out;
}

/** "pricing.pieces[3].lengthCm" → "pieces.3.lengthCm" (react-hook-form path), dropping a prefix. */
export function toFormPath(path: string, prefix = ''): string {
  const stripped = prefix && path.startsWith(prefix) ? path.slice(prefix.length) : path;
  return stripped.replace(/\[(\d+)\]/g, '.$1');
}

export async function problemFrom(response: Response): Promise<ApiError | NetworkError> {
  const type = response.headers.get('content-type') ?? '';
  if (type.includes('json')) {
    try {
      const body = (await response.json()) as {
        code?: string;
        title?: string;
        detail?: string;
        requestId?: string;
      };
      return new ApiError(
        response.status,
        body.code ?? body.title ?? `HTTP_${response.status}`,
        body.detail ?? '',
        body.requestId ?? null,
      );
    } catch {
      // fall through
    }
  }
  if (response.status >= 500 || response.status === 0)
    return new NetworkError('El servidor no está disponible en este momento');
  return new ApiError(response.status, `HTTP_${response.status}`, response.statusText);
}
